import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { DayJournal, JournalBlock, JournalState, Operation, ReviewStatus, Tag, TagCategory } from '../lib/types';
import { loadState, saveState } from '../lib/storage';
import { uid } from '../lib/id';
import { createEmptyJournal, DEFAULT_TAG_COLOR } from '../lib/defaults';
import { isTrashed, permanentDeleteAt, purgeExpired, purgeExpiredJournals } from '../lib/trash';
import { useAuth } from './AuthContext';
type JournalField = 'title' | 'followedPlan' | 'reviewStatus';
type Action = {
  type: 'add_operation';
  op: Operation;
} | {
  type: 'update_operation';
  op: Operation;
} | {
  type: 'delete_operation';
  id: string;
} | {
  type: 'restore_operation';
  id: string;
} | {
  type: 'destroy_operation';
  id: string;
} | {
  type: 'delete_day';
  date: string;
} | {
  type: 'restore_day';
  date: string;
} | {
  type: 'destroy_day';
  date: string;
} | {
  type: 'empty_trash';
} | {
  type: 'purge_expired';
} | {
  type: 'load_user';
  state: JournalState;
} | {
  type: 'add_category';
  name: string;
} | {
  type: 'update_category';
  id: string;
  patch: Partial<TagCategory>;
} | {
  type: 'delete_category';
  id: string;
} | {
  type: 'reorder_categories';
  ids: string[];
} | {
  type: 'add_tag';
  categoryId: string;
  label: string;
  color: string;
} | {
  type: 'update_tag';
  id: string;
  patch: Partial<Tag>;
} | {
  type: 'delete_tag';
  id: string;
} | {
  type: 'reorder_tags';
  categoryId: string;
  ids: string[];
} | {
  type: 'update_day_journal_field';
  date: string;
  field: JournalField;
  value: string | boolean | null;
} | {
  type: 'insert_journal_blocks';
  date: string;
  blocks: JournalBlock[];
  afterId?: string | null;
} | {
  type: 'update_journal_block';
  date: string;
  blockId: string;
  patch: Partial<JournalBlock>;
} | {
  type: 'delete_journal_block';
  date: string;
  blockId: string;
} | {
  type: 'reorder_journal_blocks';
  date: string;
  blockIds: string[];
};
function getJournal(state: JournalState, date: string): DayJournal {
  return state.dayJournals[date] ?? createEmptyJournal(date, state.userId);
}
function ordered(blocks: JournalBlock[]): JournalBlock[] {
  return [...blocks].sort((a, b) => a.order - b.order);
}
function writeJournal(state: JournalState, date: string, journal: DayJournal): JournalState {
  return {
    ...state,
    dayJournals: {
      ...state.dayJournals,
      [date]: {
        ...journal,
        userId: state.userId,
        updatedAt: Date.now()
      }
    }
  };
}
function reducer(state: JournalState, action: Action): JournalState {
  switch (action.type) {
    case 'load_user':
      return action.state;
    case 'add_operation':
      // Ownership is stamped server-side style: the record always belongs to
      // the authenticated account that created it.
      return {
        ...state,
        operations: [...state.operations, {
          ...action.op,
          userId: state.userId
        }]
      };
    case 'update_operation':
      return {
        ...state,
        operations: state.operations.map(operation => operation.id === action.op.id && operation.userId === state.userId ? {
          ...action.op,
          userId: state.userId
        } : operation)
      };
    case 'delete_operation':
      {
        // Soft delete: kept with every image, note and tag for 30 days.
        const deletedAt = Date.now();
        return {
          ...state,
          operations: state.operations.map(operation => operation.id === action.id ? {
            ...operation,
            deletedAt,
            permanentDeleteAt: permanentDeleteAt(deletedAt),
            trashedWithDay: null
          } : operation)
        };
      }
    case 'restore_operation':
      return {
        ...state,
        operations: state.operations.map(operation => operation.id === action.id ? {
          ...operation,
          deletedAt: null,
          permanentDeleteAt: null,
          trashedWithDay: null
        } : operation)
      };
    case 'destroy_operation':
      return {
        ...state,
        operations: state.operations.filter(operation => operation.id !== action.id)
      };
    case 'delete_day':
      {
        // The whole day travels together: journal + notes + images + operations.
        const deletedAt = Date.now();
        const expiry = permanentDeleteAt(deletedAt);
        const journal = getJournal(state, action.date);
        return {
          ...state,
          operations: state.operations.map(operation => operation.date === action.date && !isTrashed(operation) ? {
            ...operation,
            deletedAt,
            permanentDeleteAt: expiry,
            trashedWithDay: action.date
          } : operation),
          dayJournals: {
            ...state.dayJournals,
            [action.date]: {
              ...journal,
              userId: state.userId,
              deletedAt,
              permanentDeleteAt: expiry
            }
          }
        };
      }
    case 'restore_day':
      {
        const journal = state.dayJournals[action.date];
        return {
          ...state,
          operations: state.operations.map(operation => operation.trashedWithDay === action.date ? {
            ...operation,
            deletedAt: null,
            permanentDeleteAt: null,
            trashedWithDay: null
          } : operation),
          dayJournals: journal ? {
            ...state.dayJournals,
            [action.date]: {
              ...journal,
              deletedAt: null,
              permanentDeleteAt: null
            }
          } : state.dayJournals
        };
      }
    case 'destroy_day':
      {
        const dayJournals = {
          ...state.dayJournals
        };
        delete dayJournals[action.date];
        return {
          ...state,
          operations: state.operations.filter(operation => operation.trashedWithDay !== action.date),
          dayJournals
        };
      }
    case 'empty_trash':
      {
        const dayJournals: Record<string, DayJournal> = {};
        for (const [date, journal] of Object.entries(state.dayJournals)) {
          if (!isTrashed(journal)) dayJournals[date] = journal;
        }
        return {
          ...state,
          operations: state.operations.filter(operation => !isTrashed(operation)),
          dayJournals
        };
      }
    case 'purge_expired':
      {
        const operations = purgeExpired(state.operations);
        const dayJournals = purgeExpiredJournals(state.dayJournals);
        const unchanged = operations.length === state.operations.length && Object.keys(dayJournals).length === Object.keys(state.dayJournals).length;
        return unchanged ? state : {
          ...state,
          operations,
          dayJournals
        };
      }
    case 'add_category':
      {
        const category: TagCategory = {
          id: uid('cat'),
          name: action.name,
          multiple: false,
          order: state.categories.length
        };
        return {
          ...state,
          categories: [...state.categories, category]
        };
      }
    case 'update_category':
      return {
        ...state,
        categories: state.categories.map(category => category.id === action.id ? {
          ...category,
          ...action.patch
        } : category)
      };
    case 'delete_category':
      return {
        ...state,
        categories: state.categories.filter(category => category.id !== action.id),
        tags: state.tags.filter(tag => tag.categoryId !== action.id),
        operations: state.operations.map(operation => ({
          ...operation,
          tagIds: operation.tagIds.filter(id => state.tags.find(tag => tag.id === id)?.categoryId !== action.id)
        }))
      };
    case 'reorder_categories':
      return {
        ...state,
        categories: state.categories.map(category => ({
          ...category,
          order: action.ids.indexOf(category.id)
        })).sort((a, b) => a.order - b.order)
      };
    case 'add_tag':
      {
        const tag: Tag = {
          id: uid('tag'),
          categoryId: action.categoryId,
          label: action.label,
          color: action.color,
          order: state.tags.filter(item => item.categoryId === action.categoryId).length
        };
        return {
          ...state,
          tags: [...state.tags, tag]
        };
      }
    case 'update_tag':
      return {
        ...state,
        tags: state.tags.map(tag => tag.id === action.id ? {
          ...tag,
          ...action.patch
        } : tag)
      };
    case 'delete_tag':
      return {
        ...state,
        tags: state.tags.filter(tag => tag.id !== action.id),
        operations: state.operations.map(operation => ({
          ...operation,
          tagIds: operation.tagIds.filter(id => id !== action.id)
        }))
      };
    case 'reorder_tags':
      return {
        ...state,
        tags: state.tags.map(tag => tag.categoryId === action.categoryId ? {
          ...tag,
          order: action.ids.indexOf(tag.id)
        } : tag).sort((a, b) => a.order - b.order)
      };
    case 'update_day_journal_field':
      {
        const journal = getJournal(state, action.date);
        return writeJournal(state, action.date, {
          ...journal,
          [action.field]: action.value
        } as DayJournal);
      }
    case 'insert_journal_blocks':
      {
        const journal = getJournal(state, action.date);
        const existing = ordered(journal.blocks);
        const index = action.afterId ? existing.findIndex(block => block.id === action.afterId) + 1 : existing.length;
        const safeIndex = index < 0 ? existing.length : index;
        const merged = [...existing.slice(0, safeIndex), ...action.blocks, ...existing.slice(safeIndex)].map((block, order) => ({
          ...block,
          order
        }));
        return writeJournal(state, action.date, {
          ...journal,
          blocks: merged
        });
      }
    case 'update_journal_block':
      {
        const journal = getJournal(state, action.date);
        return writeJournal(state, action.date, {
          ...journal,
          blocks: journal.blocks.map(block => block.id === action.blockId ? {
            ...block,
            ...action.patch
          } : block)
        });
      }
    case 'delete_journal_block':
      {
        const journal = getJournal(state, action.date);
        return writeJournal(state, action.date, {
          ...journal,
          blocks: journal.blocks.filter(block => block.id !== action.blockId)
        });
      }
    case 'reorder_journal_blocks':
      {
        const journal = getJournal(state, action.date);
        const blocks = action.blockIds.map((id, order) => {
          const block = journal.blocks.find(item => item.id === id);
          return block ? {
            ...block,
            order
          } : null;
        }).filter((block): block is JournalBlock => !!block);
        return writeJournal(state, action.date, {
          ...journal,
          blocks
        });
      }
    default:
      return state;
  }
}

/** Real persistence state, reported by lib/storage.ts. */
export type SyncStatus = 'idle' | 'saving' | 'saved' | 'error';
interface JournalContextValue {
  state: JournalState;
  loading: boolean;
  /** 'saved' only when Supabase actually accepted the write. */
  syncStatus: SyncStatus;
  syncError: string | null;
  /** Retries the last failed save with the current data. */
  retrySave: () => void;
  /** Operations excluding the trash — the only source for calculations. */
  activeOperations: Operation[];
  trashedOperations: Operation[];
  /** Day journals excluding trashed days. */
  activeJournals: DayJournal[];
  trashedJournals: DayJournal[];
  /** True when the whole day sits in the trash. */
  isDayTrashed: (date: string) => boolean;
  addOperation: (op: Operation) => void;
  updateOperation: (op: Operation) => void;
  deleteOperation: (id: string) => void;
  restoreOperation: (id: string) => void;
  destroyOperation: (id: string) => void;
  deleteDay: (date: string) => void;
  restoreDay: (date: string) => void;
  destroyDay: (date: string) => void;
  emptyTrash: () => void;
  addCategory: (name: string) => void;
  renameCategory: (id: string, name: string) => void;
  updateCategory: (id: string, patch: Partial<TagCategory>) => void;
  deleteCategory: (id: string) => void;
  reorderCategories: (ids: string[]) => void;
  addTag: (categoryId: string, label: string, color?: string) => void;
  updateTag: (id: string, patch: Partial<Tag>) => void;
  deleteTag: (id: string) => void;
  reorderTags: (categoryId: string, ids: string[]) => void;
  /** How many active operations still reference this tag. */
  tagUsageCount: (tagId: string) => number;
  getDayJournal: (date: string) => DayJournal;
  updateDayJournalField: (date: string, field: JournalField, value: string | boolean | null) => void;
  setReviewStatus: (date: string, status: ReviewStatus) => void;
  insertJournalBlocks: (date: string, blocks: JournalBlock[], afterId?: string | null) => void;
  updateJournalBlock: (date: string, blockId: string, patch: Partial<JournalBlock>) => void;
  deleteJournalBlock: (date: string, blockId: string) => void;
  reorderJournalBlocks: (date: string, blockIds: string[]) => void;
  categoriesSorted: TagCategory[];
  tagsByCategory: (categoryId: string) => Tag[];
}
const JournalContext = createContext<JournalContextValue | null>(null);
const EMPTY_STATE = (userId: string): JournalState => ({
  userId,
  operations: [],
  categories: [],
  tags: [],
  dayJournals: {}
});
export function JournalProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user
  } = useAuth();
  const userId = user?.id ?? '';
  const [state, dispatch] = useReducer(reducer, userId, EMPTY_STATE);
  const [loading, setLoading] = React.useState(true);
  const [syncStatus, setSyncStatus] = React.useState<SyncStatus>('idle');
  const [syncError, setSyncError] = React.useState<string | null>(null);
  const [retryToken, setRetryToken] = React.useState(0);
  const loadedRef = React.useRef(false);

  // Swap the whole dataset when the session changes, so no record ever leaks
  // between accounts.
  useEffect(() => {
    let active = true;
    loadedRef.current = false;
    setSyncStatus('idle');
    setSyncError(null);
    setLoading(true);
    if (!userId) {
      dispatch({
        type: 'load_user',
        state: EMPTY_STATE('')
      });
      setLoading(false);
      return () => {
        active = false;
      };
    }
    Promise.resolve(loadState(userId)).then(loaded => {
      if (!active) return;
      dispatch({
        type: 'load_user',
        state: loaded
      });
      loadedRef.current = true;
      setLoading(false);
      dispatch({
        type: 'purge_expired'
      });
    });
    return () => {
      active = false;
    };
  }, [userId]);

  // Debounced persistence: only writes once the user's dataset is loaded, so a
  // pending load can never overwrite stored data with an empty state. The real
  // result from Supabase drives syncStatus, so the UI never shows "Guardado"
  // when the write was rejected (for example by RLS).
  useEffect(() => {
    if (!userId || state.userId !== userId || !loadedRef.current) return;
    let active = true;
    setSyncStatus('saving');
    const timeout = setTimeout(() => {
      saveState(userId, state).then(() => {
        if (!active) return;
        setSyncStatus('saved');
        setSyncError(null);
      }).catch((error: unknown) => {
        if (!active) return;
        setSyncStatus('error');
        setSyncError(error instanceof Error && error.message ? error.message : 'No se han podido guardar los cambios.');
      });
    }, 600);
    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [userId, state, retryToken]);
  const retrySave = React.useCallback(() => setRetryToken(token => token + 1), []);
  const value = useMemo<JournalContextValue>(() => {
    const categoriesSorted = [...state.categories].sort((a, b) => a.order - b.order);
    const owned = (record: {
      userId?: string;
    }) => !record.userId || record.userId === state.userId;
    const journals = Object.values(state.dayJournals).filter(owned);
    const trashedDays = new Set(journals.filter(isTrashed).map(journal => journal.date));

    // Every calculation reads from here: current user only, trash excluded,
    // and days moved to the trash are excluded as a whole.
    const activeOperations = state.operations.filter(operation => owned(operation) && !isTrashed(operation) && !trashedDays.has(operation.date));
    return {
      state,
      loading,
      syncStatus,
      syncError,
      retrySave,
      activeOperations,
      trashedOperations: state.operations.filter(operation => owned(operation) && isTrashed(operation) && !operation.trashedWithDay).sort((a, b) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0)),
      activeJournals: journals.filter(journal => !isTrashed(journal)),
      trashedJournals: journals.filter(isTrashed).sort((a, b) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0)),
      isDayTrashed: date => trashedDays.has(date),
      addOperation: op => dispatch({
        type: 'add_operation',
        op
      }),
      updateOperation: op => dispatch({
        type: 'update_operation',
        op
      }),
      deleteOperation: id => dispatch({
        type: 'delete_operation',
        id
      }),
      restoreOperation: id => dispatch({
        type: 'restore_operation',
        id
      }),
      destroyOperation: id => dispatch({
        type: 'destroy_operation',
        id
      }),
      deleteDay: date => dispatch({
        type: 'delete_day',
        date
      }),
      restoreDay: date => dispatch({
        type: 'restore_day',
        date
      }),
      destroyDay: date => dispatch({
        type: 'destroy_day',
        date
      }),
      emptyTrash: () => dispatch({
        type: 'empty_trash'
      }),
      addCategory: name => dispatch({
        type: 'add_category',
        name
      }),
      renameCategory: (id, name) => dispatch({
        type: 'update_category',
        id,
        patch: {
          name
        }
      }),
      updateCategory: (id, patch) => dispatch({
        type: 'update_category',
        id,
        patch
      }),
      deleteCategory: id => dispatch({
        type: 'delete_category',
        id
      }),
      reorderCategories: ids => dispatch({
        type: 'reorder_categories',
        ids
      }),
      addTag: (categoryId, label, color) => dispatch({
        type: 'add_tag',
        categoryId,
        label,
        color: color ?? DEFAULT_TAG_COLOR
      }),
      updateTag: (id, patch) => dispatch({
        type: 'update_tag',
        id,
        patch
      }),
      deleteTag: id => dispatch({
        type: 'delete_tag',
        id
      }),
      reorderTags: (categoryId, ids) => dispatch({
        type: 'reorder_tags',
        categoryId,
        ids
      }),
      tagUsageCount: tagId => state.operations.filter(operation => operation.tagIds.includes(tagId)).length,
      getDayJournal: date => getJournal(state, date),
      updateDayJournalField: (date, field, value) => dispatch({
        type: 'update_day_journal_field',
        date,
        field,
        value
      }),
      setReviewStatus: (date, status) => dispatch({
        type: 'update_day_journal_field',
        date,
        field: 'reviewStatus',
        value: status
      }),
      insertJournalBlocks: (date, blocks, afterId) => dispatch({
        type: 'insert_journal_blocks',
        date,
        blocks,
        afterId
      }),
      updateJournalBlock: (date, blockId, patch) => dispatch({
        type: 'update_journal_block',
        date,
        blockId,
        patch
      }),
      deleteJournalBlock: (date, blockId) => dispatch({
        type: 'delete_journal_block',
        date,
        blockId
      }),
      reorderJournalBlocks: (date, blockIds) => dispatch({
        type: 'reorder_journal_blocks',
        date,
        blockIds
      }),
      categoriesSorted,
      tagsByCategory: categoryId => state.tags.filter(tag => tag.categoryId === categoryId && !tag.archived).sort((a, b) => a.order - b.order)
    };
  }, [state, loading, syncStatus, syncError, retrySave]);
  return <JournalContext.Provider value={value} data-id="element-591" data-mp="t0289a8uwhv7">{children}</JournalContext.Provider>;
}
export function useJournal(): JournalContextValue {
  const context = useContext(JournalContext);
  if (!context) throw new Error('useJournal must be used within JournalProvider');
  return context;
}